import { useState, useEffect, useRef } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import axios from "axios";
import { useConfigContext } from "../ConfigContext";
import grassApi from "@/services/agribot/grassland";
import { ApiContext } from "./context";

export const ApiProvider = ({ children }) => {
  const { apiEp } = useConfigContext();
  const {
    isAuthenticated,
    user,
    getAccessTokenSilently,
    getAccessTokenWithPopup,
    logout,
  } = useAuth0();
  const [api, setApi] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const apiRef = useRef(null);

  // Stable refs so the interceptor always sees the latest SDK functions
  // without needing to tear down and re-create the axios instance.
  const getTokenRef = useRef(getAccessTokenSilently);
  const getTokenPopupRef = useRef(getAccessTokenWithPopup);
  const logoutRef = useRef(logout);
  useEffect(() => {
    getTokenRef.current = getAccessTokenSilently;
  }, [getAccessTokenSilently]);
  useEffect(() => {
    getTokenPopupRef.current = getAccessTokenWithPopup;
  }, [getAccessTokenWithPopup]);
  useEffect(() => {
    logoutRef.current = logout;
  }, [logout]);

  useEffect(() => {
    if (!isAuthenticated) {
      apiRef.current = null;
      setApi(null);
      setError(null);
      setLoading(false);
      return;
    }

    const setUpApi = async () => {
      setLoading(true);
      setError(null);

      try {
        const axiosInstance = axios.create({
          baseURL: apiEp,
          timeout: 70000,
          headers: {
            "Content-Type": "application/json",
          },
        });

        // Request interceptor: attach a fresh token to every outgoing request.
        // Auth0 SDK returns the cached token if still valid and silently
        // refreshes via the refresh token when it has expired.
        axiosInstance.interceptors.request.use(async (config) => {
          try {
            const token = await getTokenRef.current();
            config.headers.Authorization = `Bearer ${token}`;
          } catch (err) {
            // On localhost Auth0 may require consent via popup (first-party
            // verification is unavailable). This only happens in development.
            if (err.error === "consent_required") {
              try {
                const token = await getTokenPopupRef.current();
                config.headers.Authorization = `Bearer ${token}`;
                return config;
              } catch {
                // Popup also failed — fall through to session-expiry check.
              }
            }

            const isSessionExpired =
              err.error === "login_required" ||
              err.error === "invalid_grant" ||
              err.error === "missing_refresh_token";

            if (isSessionExpired) {
              logoutRef.current({
                logoutParams: { returnTo: window.location.origin },
              });
            }
            throw err;
          }
          return config;
        });

        apiRef.current = new grassApi({ axios: axiosInstance });

        const e2eMode = import.meta.env.VITE_E2E_MODE === "true";
        if (e2eMode) {
          const e2eApiSecret = (
            import.meta.env.VITE_E2E_API_SECRET || ""
          ).trim();
          const e2eSub = (
            import.meta.env.VITE_E2E_SUB ||
            user?.sub ||
            ""
          ).trim();
          const e2eEmail = (
            import.meta.env.VITE_E2E_EMAIL ||
            user?.email ||
            ""
          ).trim();

          const missingConfig = [];
          if (!e2eApiSecret) missingConfig.push("VITE_E2E_API_SECRET");
          if (!e2eSub) missingConfig.push("VITE_E2E_SUB (or Auth0 user.sub)");
          if (!e2eEmail)
            missingConfig.push("VITE_E2E_EMAIL (or Auth0 user.email)");

          if (missingConfig.length > 0) {
            const message = `E2E mode is enabled but required E2E auth config is missing: ${missingConfig.join(", ")}`;
            console.error(message);
            throw new Error(message);
          }

          apiRef.current.axios.defaults.headers.common["x-e2e-secret"] =
            e2eApiSecret;
          apiRef.current.axios.defaults.headers.common["x-e2e-sub"] = e2eSub;
          apiRef.current.axios.defaults.headers.common["x-e2e-email"] =
            e2eEmail;
        }

        setApi(apiRef.current);
      } catch (err) {
        console.error("Failed to setup API client:", err);
        setError("Failed to initialize API client");
        setApi(null);
      } finally {
        setLoading(false);
      }
    };

    setUpApi();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiEp, isAuthenticated, user?.sub, user?.email]);

  return (
    <ApiContext.Provider value={{ api, loading, error }}>
      {children}
    </ApiContext.Provider>
  );
};
